import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { BrowserService } from '@core/services/storage.service';
import { UserProfileService } from '@services/user-profile.service';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@states/auth/auth.selectors';

/**
 * Onboarding guard - protects the onboarding page
 * - Redirects unauthenticated users to sign-in
 * - Allows users who haven't completed onboarding
 * - Redirects users who have completed onboarding to home
 */
export const onboardingGuard: CanActivateFn = async (_route, state) => {
  const store = inject(Store);
  const userProfileService = inject(UserProfileService);
  const router = inject(Router);

  const user = store.selectSnapshot(AuthSelectors.getUser);

  // Not authenticated - redirect to sign-in
  if (!user) {
    return router.createUrlTree(['/sign-in'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Check if user has completed onboarding
  let profile;
  try {
    profile = await userProfileService.getUserProfile(user.id);
  } catch {
    // On Firestore error treat as incomplete - allow access to onboarding
    return true;
  }

  // No profile or onboarding not complete - allow access
  if (!profile || !profile.onboardingCompletedAt) {
    return true;
  }

  // Onboarding already complete - redirect to home
  return router.createUrlTree(['/']);
};

/**
 * Profile completion guard - ensures user has completed onboarding
 * Used on routes that require a complete profile (e.g., checkout, messaging)
 */
export const profileCompleteGuard: CanActivateFn = async (_route, state) => {
  const store = inject(Store);
  const userProfileService = inject(UserProfileService);
  const router = inject(Router);
  const browser = inject(BrowserService);

  const user = store.selectSnapshot(AuthSelectors.getUser);

  // Not authenticated - redirect to sign-in
  if (!user) {
    return router.createUrlTree(['/sign-in'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Check if user has completed onboarding
  let profile;
  try {
    profile = await userProfileService.getUserProfile(user.id);
  } catch {
    // On Firestore error redirect to onboarding as a safe fallback
    browser.setSession('onboarding-return-url', state.url);
    return router.createUrlTree(['/onboarding']);
  }

  // No profile or onboarding not complete - redirect to onboarding
  if (!profile || !profile.onboardingCompletedAt) {
    // Store the intended destination
    browser.setSession('onboarding-return-url', state.url);

    return router.createUrlTree(['/onboarding']);
  }

  // Profile complete - allow access
  return true;
};
